/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { KeyValuePair, SeeMoreSummary, ThemeTokens } from '@acp/core-ui';
import { ProblemData, ActiveNoticePropertyTag } from '@acp/shared';
import {
    BorderProps, Card, CardBody, CardBodyProps, GridItem, GridItemProps,
    Hide,
    HStack,
    Link,
    Show,
    Text,
    Tooltip,
    VStack,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Box,
} from '@chakra-ui/react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { AccessIssueProblemList } from '../problem-list';

const gap = 2;

const gridAreaProps: { [key: string]: GridItemProps } = {
    address: {
        gridArea: 'address',
        as: VStack,
        alignItems: 'start',
        justifyContent: 'flex-start',
        gap,
    },
    occupant: {
        gridArea: 'occupant',
    },
    reason: {
        gridArea: 'reason',
    },
    status: {
        gridArea: 'status',
    },
    actions: {
        gridArea: 'actions',
        as: HStack,
        justifySelf: 'flex-end',
        gap,
    },
};

type AreaType = keyof typeof gridAreaProps;

interface RowColumnProps extends GridItemProps {
    area: AreaType;
}

export function RowLayout(
    {
        children,
        borderLeftColor = 'secondary.500',
    }:
    {
        children: React.ReactNode;
        borderLeftColor?: BorderProps['borderLeftColor'];
    }
) {
    return (
        <Card
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderLeftWidth={6}
            borderLeftColor={borderLeftColor}
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            {children}
        </Card>
    );
}

function RowBody({ children, as: Component, ...props }: CardBodyProps) {
    return (
        <CardBody as={Component} {...props}>
            {children}
        </CardBody>
    );
}

function RowColumn({ children, area, ...props }: RowColumnProps) {
    const gridItemAreaProps = gridAreaProps[area];

    return (
        <GridItem {...gridItemAreaProps} {...props}>
            {children}
        </GridItem>
    );
}

function Address({
    propertyId,
    addressLine1,
    addressLine2,
    tags,
}: {
    propertyId: number;
    addressLine1: string;
    addressLine2: string;
    tags: string[];
}) {
    return (
        <Link
            as={RouterLink}
            to={`/property/${propertyId}`}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
            fontWeight="medium"
            color={
                !tags.includes(ActiveNoticePropertyTag.Cancelled)
                    ? 'black'
                    : 'muted.500'
            }
        >
            <VStack alignItems="start" gap={0}>
                <Text>{addressLine1}</Text>
                <Text>{addressLine2}</Text>
            </VStack>
        </Link>
    );
}

function ReasonSummary(
    { problems }:
    { problems: ProblemData[] }
) {
    const firstProblem = problems?.[0] || [];
    const { publicName } = firstProblem;
    const remainingOccupantCount = problems?.length
        ? Math.max(problems.length - 1, 0)
        : 0;

    if (!publicName) {
        return null;
    }

    return (
        <Popover trigger="hover" isLazy>
            <PopoverTrigger>
                <Box maxW="fit-content">
                    <SeeMoreSummary
                        name={publicName}
                        remainingCount={remainingOccupantCount}
                        hasIcon={false}
                    />
                </Box>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverBody>
                    <VStack align="left" gap={2}>
                        <Box>
                            <Text fontSize="small" fontWeight="bold">
                                Reason
                            </Text>
                            <AccessIssueProblemList problems={problems} />
                        </Box>
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

function Reason(
    { problems }:
    { problems: ProblemData[] }
) {
    if (problems.length === 0) {
        return (
            <>
                <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                    <Text noOfLines={1} pr={1}>Complex problem</Text>
                    <Text fontSize="sm" color="muted.500">Please contact DI to help resolve the issue</Text>
                </Show>
                <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                    <KeyValuePair
                        label="Reason"
                        align="flex-start"
                        spacing={0}
                    >
                        <Text noOfLines={1} pr={1}>Complex problem</Text>
                    </KeyValuePair>
                </Hide>
            </>
        );
    }

    const firstProblem = problems?.[0] || [];

    return (
        <>
            <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <ReasonSummary problems={problems} />
                <Tooltip label={`Notified on ${format(parseISO(firstProblem.createdAt), 'dd/MM/yyyy')}`} bgColor="white" color="black">
                    <Text mt={1} fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                        {`Notified ${formatDistanceToNow(parseISO(firstProblem.createdAt), { addSuffix: true })}`}
                    </Text>
                </Tooltip>
            </Show>
            <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <KeyValuePair
                    label="Reason"
                    align="flex-start"
                    spacing={0}
                >
                    <ReasonSummary problems={problems} />
                    <Tooltip label={`Notified on ${format(parseISO(firstProblem.createdAt), 'dd/MM/yyyy')}`} bgColor="white" color="black">
                        <Text fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                            {`Notified ${formatDistanceToNow(parseISO(firstProblem.createdAt), { addSuffix: true })}`}
                        </Text>
                    </Tooltip>
                </KeyValuePair>
            </Hide>
        </>
    );
}

RowLayout.Body = RowBody;
RowLayout.Column = RowColumn;
RowLayout.Address = Address;
RowLayout.Reason = Reason;
